import React from 'react';
import {
  Box,
  Heading,
  Text,
  Icon,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaTachometerAlt, FaBrain } from 'react-icons/fa';

const OwnerSummaryCard = ({ aiScore = 0 }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const score = aiScore; // use passed ai_score
  const minScore = 0;
  const maxScore = 100;

  const valuationData = [
    {
      valuation: 'Excellent',
      definition:
        'Vehicle is brand new (age), Mileage is very low <10,000 miles for age, Car has OEM/Aftermarket warranty, 1 owner with complete OEM vehicle maintenance records.',
      scoreRange: '86-100',
    },
    {
      valuation: 'Good',
      definition:
        'Vehicle is fairly new (age 1-4 years old), Low mileage <10,000-25,000 is very low for age, Car has OEM/Aftermarket warranty, 1-2 owners. Complete vehicle OEM and aftermarket maintenance records present.',
      scoreRange: '70-85',
    },
    {
      valuation: 'Average',
      definition:
        'Vehicle is older (5+ years old), avg. annual mileage of 14,000 overall mileage <75,000-200,000, Car has aftermarket warranty or no warranty at all, 2-4 owners. Vehicle OEM and aftermarket maintenance records are incomplete, open recalls, 1-2 accidents.',
      scoreRange: '50-69',
    },
    {
      valuation: 'Below Average',
      definition:
        'Vehicle is older (5-20 years old), avg. annual mileage of 20,000 overall mileage <100,000-200,000, Car has no warranty at all, 3-6 owners. Minimal vehicle maintenance records. Exterior visible damage, check engine lights present and not regularly maintained, salvaged vehicle, 2-5 accidents.',
      scoreRange: '26-49',
    },
  ];

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.300"
      borderRadius="md"
      overflow="hidden"
      bg={bg}
      boxShadow="sm"
      textAlign="center"
    >
      {/* Header */}
      <Flex
        justify={{ base: 'flex-start', md: 'space-between' }}
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: 2, md: 0 }}
        bg="#212e89"
        color="white"
        px={4}
        py={2}
      >
        <Flex align="center" gap={4}>
          <Heading size={{ base: "md", md: "lg" }} fontWeight="semibold">
            AI Score
          </Heading>
          <Icon as={FaBrain} boxSize={8} />
        </Flex>
        <Button
           variant="outline"
           borderColor="white"
           color="white"
          
          _active={{ bg: '#d4b728' }}
 colorScheme="whiteAlpha"
            size="sm"
          _hover={{
            bg: 'whiteAlpha.200',
          }}
          onClick={onOpen}
          width={{ base: '100%', md: 'auto' }}
          mt={{ base: 1, md: 0 }}
        >
          View AI Valuation Details
        </Button>
      </Flex>

      <Box p={6}>
        <Icon as={FaTachometerAlt} boxSize={8} color="blue.500" mb={4} />

        {/* Gauge */}
        <Box position="relative" w="180px" h="100px" mx="auto" mb={2}>
          <svg viewBox="0 0 200 100" width="100%" height="100%">
            <defs>
              {/* Define gradient for the arc */}
              <linearGradient
                id="arcGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset={`${score}%`} stopColor="#3B82F6" />
                <stop offset={`${score}%`} stopColor="#d9d9d9" />
              </linearGradient>
            </defs>

            {/* Single arc with gradient */}
            <path
              d="M10,100 A90,90 0 0,1 190,100"
              stroke="url(#arcGradient)"
              strokeWidth="12"
              fill="none"
              strokeLinecap="round"
            />

            {/* score indicator circle - only show if score > 0 */}
            {/* {score > 0 && (
            <circle
              cx={endX}
              cy={endY}
              r="8"
              fill="white"
              stroke="#6B46C1"
              strokeWidth="4"
            />
          )} */}
          </svg>
          {/* Range labels */}
          <Box
            position="absolute"
            top="80px"
            left="0"
            right="0"
            display="flex"
            justifyContent="space-between"
            px="6"
            fontSize="sm"
            color="gray.600"
          >
            <Text>{minScore}</Text>
            <Text>{maxScore}</Text>
          </Box>
        </Box>

        {/* Score and description */}
        <Text fontSize="4xl" fontWeight="bold" color="blue.600" mt={2}>
          {score}
        </Text>
        <Text fontSize="sm" fontWeight="bold">
          AI Score out of 100
        </Text>
        <Text fontSize="sm"  mt={3}  px={2}>
          The AI score is calculated based on car's mileage, manufactured year, car maintenance record
        </Text>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent
          maxW={{ base: '95vw', md: '64vw' }}
          w={{ base: '95vw', md: '64vw' }}
          maxH="85vh"
        >
          <ModalHeader>AI Valuation Spectrum</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <Text mb={4}>
              This spectrum explains how AI valuation tiers are derived by considering vehicle age,
              mileage, warranty coverage, ownership history, and maintenance documentation.
            </Text>
            <Box overflowX={{ base: 'auto', md: 'visible' }}>
            <Table
              variant="simple"
              size="sm"
              borderWidth="1px"
              borderColor="gray.200"
              sx={{
                // Allow natural column sizing on mobile; switch to fixed on md+
                tableLayout: ['auto', null, 'fixed'],
                'th, td': {
                  borderWidth: '1px',
                  borderColor: 'gray.200',
                  verticalAlign: 'top',
                },
                // relax fixed widths on mobile to allow natural wrapping/scroll
                '@media (min-width: 48em)': {
                  'th:nth-of-type(1), td:nth-of-type(1)': {
                    width: '20%',
                  },
                  'th:nth-of-type(2), td:nth-of-type(2)': {
                    width: '55%',
                  },
                  'th:nth-of-type(3), td:nth-of-type(3)': {
                    width: '25%',
                  },
                },
              }}
              // Force horizontal scroll on small screens instead of squeezing columns
              minW={{ base: '900px', md: 'auto' }}
            >
              <Thead bg="#212e89" sx={{ 'th': { py: 3 } }}>
                <Tr>
                  <Th color="white">AI Valuation</Th>
                  <Th color="white">Definition · Variables</Th>
                  <Th color="white">Score</Th>
                </Tr>
              </Thead>
              <Tbody>
                {valuationData.map((row) => (
                  <Tr key={row.valuation}>
                    <Td fontWeight="semibold">{row.valuation}</Td>
                    <Td>{row.definition}</Td>
                    <Td>{row.scoreRange}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OwnerSummaryCard;
